import api from './api';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  is_active: boolean;
  role?: {
    id: number;
    name: string;
    description?: string;
    permissions: Permission[];
  };
  created_at: string;
  updated_at: string;
}

export interface Permission {
  id: number;
  name: string;
  description?: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface PasswordChangeRequest {
  old_password: string;
  new_password: string;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Clear any existing tokens before attempting login
    this.clearTokens();
    
    const response = await api.post<AuthResponse>('/auth/login-json', credentials);
    
    // Store tokens in localStorage
    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);
    
    // Notify auth state change
    this._notifyAuthStateChange();
    
    return response.data;
  },

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<AuthResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });

    // Update stored tokens
    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);

    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  async updateProfile(userData: { username?: string; email?: string }): Promise<User> {
    const response = await api.put<User>('/auth/me', userData);
    return response.data;
  },

  async changePassword(passwordData: PasswordChangeRequest): Promise<void> {
    await api.post('/auth/change-password', passwordData);
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    } finally {
      this.clearTokens();
      this._notifyAuthStateChange();
      window.location.href = '/login';
    }
  },

  clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  },

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  },

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  },

  hasPermission(permissionName: string, user?: User): boolean {
    if (!user?.role?.permissions) {
      return false;
    }
    return user.role.permissions.some(permission => permission.name === permissionName);
  },

  hasAnyPermission(permissionNames: string[], user?: User): boolean {
    return permissionNames.some(permission => this.hasPermission(permission, user));
  },

  getToken(): string | null {
    return this.getAccessToken();
  },

  // Auth state change listeners
  _authStateListeners: new Set<(isAuthenticated: boolean) => void>(),

  onAuthStateChange(callback: (isAuthenticated: boolean) => void): () => void {
    this._authStateListeners.add(callback);
    
    // Call immediately with current state
    callback(this.isAuthenticated());
    
    // Return unsubscribe function
    return () => {
      this._authStateListeners.delete(callback);
    };
  },

  _notifyAuthStateChange(): void {
    const isAuthenticated = this.isAuthenticated();
    this._authStateListeners.forEach(callback => {
      try {
        callback(isAuthenticated);
      } catch (error) {
        console.error('Error in auth state change callback:', error);
      }
    });
  },
};